import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { groupBy } from "lodash";
import { toast } from "react-toastify";
import {
  loadFormsySubmissionV2,
  saveFormsyV2,
  dataChange,
} from "./supervisionScripts";
import { getMyCaseload } from "../services/soar";
import Input from "../common/Input";
import Select from "../common/Select";
import Checkbox from "../common/Checkbox";
import SupervisionHeader from "./SupervisionHeader";
import Comments from "./Comments";
import Signatures from "./Signatures";
import SearchAndSelect from "../common/SearchAndSelect";
import ExpandDiv from "../common/ExpandDiv";

function Individual(props) {
  let { supervisionLogId } = useParams();
  const [data, setData] = useState({});
  const [caseload, setCaseload] = useState([]);
  const [caseloadClient, setCaseloadClient] = useState("");
  const [adHocClient, setAdHocClient] = useState("");
  const [showDischarged, setShowDischarged] = useState(false);
  const [touched, setTouched] = useState({});
  const [timeoutId, setTimeoutId] = useState(null);
  const pdfRef = useRef(true);
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  const timeOptions = [
    { name: "0:15", _id: "15" },
    { name: "0:30", _id: "30" },
    { name: "0:45", _id: "45" },
    { name: "1:00", _id: "60" },
    { name: "1:15", _id: "75" },
    { name: "1:30", _id: "90" },
    { name: "1:45", _id: "105" },
    { name: "2:00", _id: "120" },
    { name: "2:15", _id: "135" },
    { name: "2:30", _id: "150" },
    { name: "2:45", _id: "165" },
    { name: "3:00", _id: "180" },
  ];

  useEffect(() => {
    loadForm();
  }, [supervisionLogId]);

  const loadForm = () => {
    loadFormsySubmissionV2(supervisionLogId, setData);
  };

  useEffect(() => {
    if (data.PrimaryObjectUser) {
      getMyCaseload(data.PrimaryObjectUser).then((response) => {
        if (response) {
          setCaseload(response);
        }
      });
    }
  }, [data.PrimaryObjectUser]);

  const filteredCaseload = useMemo(() => {
    const records = caseload.filter(
      (program) =>
        showDischarged ||
        (program.ccx_dischargedate === undefined &&
          program["care.ccx_expdate"] === undefined)
    );
    const grouped = Object.values(groupBy(records, "con.contactid"));
    let options = grouped.map((client) => {
      return {
        name: client[0]["con.fullname"],
        _id: client[0]["con.contactid"],
        riskLevel: client[0]["con.sor_risklevel"],
      };
    });
    options = options.sort((a, b) => a["name"].localeCompare(b["name"]));
    return options;
  }, [caseload, showDischarged]);

  const handleChange = ({ currentTarget: input }, table, index, field) => {
    dataChange(
      input,
      data,
      setData,
      touched,
      setTouched,
      timeoutId,
      setTimeoutId,
      table,
      index,
      field
    );
  };

  const isSupervisee =
    safyInfo?.user?.azureId?.toUpperCase() ===
    data?.PrimaryObjectId?.toUpperCase();

  const isSupervisor =
    safyInfo?.user?.azureId?.toUpperCase() ===
    data?.SecondaryObjectId?.toUpperCase();

  const supervisorSigned =
    data?.Related?.filter(
      (s) => s.ObjectType === "Signature" && s.ObjectJSON.role === "Supervisor"
    ).length > 0
      ? true
      : false;

  const superviseeSigned =
    data?.Related?.filter(
      (s) => s.ObjectType === "Signature" && s.ObjectJSON.role === "Supervisee"
    ).length > 0
      ? true
      : false;

  const addTableRow = (table) => {
    const obj = { ...data };
    if (!obj?.Results[table]) {
      obj.Results[table] = [];
    }
    const row = {
      item: "",
      notes: "",
    };
    if (table === "FosterHomeReview") {
      row.placementStability = "";
      row.documentation = "";
      row.concerns = "";
    }
    obj.Results[table].push(row);
    setData(obj);
  };

  const addCaseReviewClient = () => {
    if (
      (adHocClient && adHocClient !== "") ||
      (caseloadClient && caseloadClient !== "")
    ) {
      const obj = { ...data };
      if (!obj.Results.CaseReviews) {
        obj.Results.CaseReviews = [];
      }
      const review = {
        clientName: adHocClient || caseloadClient.name,
        clientId: caseloadClient._id || "",
        strengthsProgress: "",
        challengesNeeds: "",
        concerns: "",
        followup: "",
        highRisk: caseloadClient.riskLevel,
      };
      obj.Results.CaseReviews.push(review);
      setData(obj);
      setCaseloadClient("");
      setAdHocClient("");
    } else {
      toast.error("You must select a client");
    }
  };

  const openClient = (id) => {
    window.open(`${window.location.origin}/client/${id}`);
  };

  const openDashboards = () => {
    const url = `${process.env.REACT_APP_ORG}/workplace/home_dashboards.aspx`;
    window.open(url);
  };

  const openPowerBI = () => {
    const url = `https://app.powerbi.com`;
    window.open(url);
  };

  return (
    <div ref={pdfRef}>
      <SupervisionHeader
        label="Individual Supervision"
        data={data}
        canClone={true}
        canDelete={isSupervisee && data.FinalSigned === false}
        handleSave={() => {
          saveFormsyV2(data, "Update");
        }}
        pdfRef={pdfRef}
      />
      <div className="row mt-4">
        <div className="col-6">
          <Input
            name="DateSupervision"
            label="Date of Supervision"
            value={data.Results?.DateSupervision}
            type="date"
            onChange={handleChange}
            disabled={!isSupervisor || supervisorSigned}
          ></Input>
        </div>
        <div className="col-6">
          <Select
            name="LengthOfSupervision"
            label="Length of Supervision Session"
            options={timeOptions}
            value={data && data.Results?.LengthOfSupervision}
            onChange={handleChange}
            disabled={!isSupervisor || supervisorSigned}
          ></Select>
        </div>
      </div>
      <div className="row mt-2">
        <div className="col ps-4 ms-2 pt-4">
          <Checkbox
            name="CountsAsLicensure"
            label="This supervision counts towards staff licensure"
            checked={data.Results?.CountsAsLicensure === true}
            onChange={handleChange}
            disabled={!isSupervisor || supervisorSigned}
          ></Checkbox>
        </div>
        <div className="col">
          <Select
            name="LengthOfLicensure"
            label="Time towards Licensure Supervision (If Applicable)"
            options={timeOptions}
            value={data.Results?.LengthOfLicensure}
            onChange={handleChange}
            disabled={!isSupervisor || supervisorSigned}
          ></Select>
        </div>
      </div>
      <div className="border px-2 pt-2 pb-0 mb-0 rounded">
        <div className="h6">Type of Supervision</div>
        <div className="row mx-4 mt-4 mb-0">
          <div className="col">
            <Checkbox
              name="ClinicalSupervision"
              label="Clinical Supervision"
              checked={data.Results?.ClinicalSupervision === true}
              onChange={handleChange}
              disabled={!isSupervisor || supervisorSigned}
            ></Checkbox>
          </div>
          <div className="col">
            <Checkbox
              name="AdministrativeSupervision"
              label="Administrative Supervision"
              checked={data.Results?.AdministrativeSupervision === true}
              onChange={handleChange}
              disabled={!isSupervisor || supervisorSigned}
            ></Checkbox>
          </div>
          <div className="col">
            <Checkbox
              name="ManagerialSupervision"
              label="Managerial Supervision"
              checked={data.Results?.ManagerialSupervision === true}
              onChange={handleChange}
              disabled={!isSupervisor || supervisorSigned}
            ></Checkbox>
          </div>
        </div>
      </div>
      {data?.Results?.ClinicalSupervision == true && (
        <div className="my-4 border rounded p-2 bg-light">
          <div className="h5 mb-4">Clinical Supervision</div>
          <div className="h5 m-2 ms-4 ps-2">Client Reviews</div>
          <div className="row ms-4">
            <div className="col-5">
              <SearchAndSelect
                label=<div className="h6">{`Select a client from ${data.PrimaryObjectName}'s caseload (${filteredCaseload.length})`}</div>
                options={filteredCaseload}
                setSelected={(e) => {
                  setAdHocClient("");
                  setCaseloadClient(e);
                }}
              />
              <Checkbox
                name="ShowDischarged"
                label="Show Discharged Clients"
                checked={showDischarged === true}
                onChange={(e) => setShowDischarged(e.currentTarget.checked)}
                disabled={!isSupervisor || supervisorSigned}
              ></Checkbox>
            </div>
            <div className="col-1">or</div>
            <div className="col-4 h6">
              <Input
                name="adHocClient"
                label="Ad Hoc client"
                value={adHocClient}
                onChange={(e) => {
                  setCaseloadClient("");
                  setAdHocClient(e.currentTarget.value);
                }}
                disabled={!isSupervisor || supervisorSigned}
              />
            </div>
            <div className="col-2 pt-2">
              {isSupervisor && !supervisorSigned && (
                <div
                  className="btn btn-success mt-4 me-2 float-end"
                  onClick={() => {
                    addCaseReviewClient();
                    setAdHocClient("");
                    setCaseloadClient("");
                  }}
                  disabled={!isSupervisor || supervisorSigned}
                >
                  Add
                </div>
              )}
            </div>
          </div>
          <div className="mx-4 mb-4">
            {data && data.Results.CaseReviews
              ? data.Results.CaseReviews.map((item, index) => {
                  return (
                    <div className="border bg-white mb-4" key={`item${index}`}>
                      <ExpandDiv
                        expandStatus={true}
                        title={
                          <div className="row rounded h5 mx-4">
                            <div className="col-11 rounded d-flex text-decoration-underline px-4 bg-safy2-1 text-white py-1">
                              {item.clientId ? (
                                <div
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    openClient(item.clientId);
                                  }}
                                >
                                  {item.clientName}
                                </div>
                              ) : (
                                item.clientName
                              )}

                              {item.highRisk?.["@name"] === "High" ? (
                                <div className="rounded bg-danger text-white px-2 ms-4 float-start">
                                  High Risk
                                </div>
                              ) : null}
                            </div>

                            <div className="col-1">
                              {isSupervisor && !supervisorSigned && (
                                <i
                                  className="fas fa-times-circle fa-lg text-danger float-end mt-1 p-2"
                                  onClick={(e) => {
                                    let obj = { ...data };
                                    obj.Results.CaseReviews.splice(index, 1);
                                    setData(obj);
                                  }}
                                  disabled={!isSupervisor || supervisorSigned}
                                ></i>
                              )}
                            </div>
                          </div>
                        }
                      >
                        <div className="m-2">
                          <Input
                            name={`${index}strengthsProgress`}
                            label="Strengths and Progress"
                            onChange={(e) =>
                              handleChange(
                                e,
                                "CaseReviews",
                                index,
                                "strengthsProgress"
                              )
                            }
                            value={
                              data.Results.CaseReviews[index][
                                "strengthsProgress"
                              ]
                            }
                            disabled={!isSupervisor || supervisorSigned}
                          />
                        </div>
                        <div className="m-2">
                          <Input
                            name={`${index}challengesNeeds`}
                            label="Challenges and Needs"
                            onChange={(e) =>
                              handleChange(
                                e,
                                "CaseReviews",
                                index,
                                "challengesNeeds"
                              )
                            }
                            value={
                              data.Results.CaseReviews[index]["challengesNeeds"]
                            }
                            disabled={!isSupervisor || supervisorSigned}
                          />
                        </div>
                        <div className="m-2">
                          <Input
                            name={`${index}concerns`}
                            label="Current Risk/Safety Concerns"
                            onChange={(e) =>
                              handleChange(e, "CaseReviews", index, "concerns")
                            }
                            value={data.Results.CaseReviews[index]["concerns"]}
                            disabled={!isSupervisor || supervisorSigned}
                          />
                        </div>
                        <div className="m-2">
                          <Input
                            name={`${index}followup`}
                            label="Next Steps/Follow-Up"
                            onChange={(e) =>
                              handleChange(e, "CaseReviews", index, "followup")
                            }
                            value={data.Results.CaseReviews[index]["followup"]}
                            disabled={!isSupervisor || supervisorSigned}
                          />
                        </div>
                      </ExpandDiv>
                    </div>
                  );
                })
              : null}
            <div className="mb-4">
              <ExpandDiv
                expandStatus={true}
                title={
                  <div className="h5 d-inline ">{`Foster Home Reviews ${
                    data?.Results?.FosterHomeReview?.length > 0
                      ? `( ${data?.Results?.FosterHomeReview?.length} )`
                      : ""
                  } `}</div>
                }
              >
                <div>
                  {data?.Results?.FosterHomeReview &&
                    data?.Results?.FosterHomeReview?.map((item, index) => (
                      <ExpandDiv
                        key={`df${index}`}
                        className="ms-4 ps-4 border bg-white mb-4"
                        expandStatus={true}
                        title={
                          <div className="row rounded h5 mx-4">
                            <div className="col-11 rounded d-flex px-4 bg-safy2-1 text-white py-1">
                              {data.Results.FosterHomeReview[index]["item"]}
                            </div>
                            <div className="col-1">
                              {isSupervisor && !supervisorSigned && (
                                <i
                                  className="fas fa-times-circle fa-lg text-danger float-end p-2"
                                  onClick={(e) => {
                                    let obj = { ...data };
                                    obj.Results.FosterHomeReview.splice(
                                      index,
                                      1
                                    );
                                    setData(obj);
                                  }}
                                  disabled={!isSupervisor || supervisorSigned}
                                ></i>
                              )}
                            </div>
                          </div>
                        }
                      >
                        <div className="p-2">
                          <Input
                            name={`${index}fhItem`}
                            label="Foster Home Name"
                            value={data.Results.FosterHomeReview[index]["item"]}
                            onChange={(e) =>
                              handleChange(e, "FosterHomeReview", index, "item")
                            }
                            disabled={!isSupervisor || supervisorSigned}
                          />
                        </div>
                        <div className="p-2">
                          <Input
                            name={`${index}fhplacement`}
                            label="Placement Stability"
                            onChange={(e) =>
                              handleChange(
                                e,
                                "FosterHomeReview",
                                index,
                                "placementStability"
                              )
                            }
                            value={
                              data?.Results?.FosterHomeReview[index][
                                "placementStability"
                              ]
                            }
                            disabled={!isSupervisor || supervisorSigned}
                          />
                        </div>
                        <div className="p-2">
                          <Input
                            name={`${index}fhdocumentatin`}
                            label="Documentation"
                            onChange={(e) =>
                              handleChange(
                                e,
                                "FosterHomeReview",
                                index,
                                "documentation"
                              )
                            }
                            value={
                              data?.Results?.FosterHomeReview[index][
                                "documentation"
                              ]
                            }
                            disabled={!isSupervisor || supervisorSigned}
                          />
                        </div>
                        <div className="p-2">
                          <Input
                            name={`${index}fhconcerns`}
                            label="Current Risk/Safety Concerns"
                            onChange={(e) =>
                              handleChange(
                                e,
                                "FosterHomeReview",
                                index,
                                "concerns"
                              )
                            }
                            value={
                              data?.Results?.FosterHomeReview[index]["concerns"]
                            }
                            disabled={!isSupervisor || supervisorSigned}
                          />
                        </div>
                        <div className="p-2">
                          <Input
                            name={`${index}fhnotes`}
                            label="Next Steps/Follow-Up"
                            onChange={(e) =>
                              handleChange(
                                e,
                                "FosterHomeReview",
                                index,
                                "notes"
                              )
                            }
                            value={
                              data?.Results?.FosterHomeReview[index]["notes"]
                            }
                            disabled={!isSupervisor || supervisorSigned}
                          />
                        </div>
                      </ExpandDiv>
                    ))}
                </div>
                <div className="row">
                  <div className="col-10">
                    {isSupervisor && !supervisorSigned && (
                      <div
                        className="btn btn-success"
                        onClick={() => addTableRow("FosterHomeReview")}
                        disabled={!isSupervisor || supervisorSigned}
                      >
                        Add
                      </div>
                    )}
                  </div>
                  <div className="col-2"></div>
                </div>
              </ExpandDiv>
            </div>
            <div className="row py-2 mt-4">
              <div className="col-3 h5">Documentation Fidelity</div>
              <div className="col-9">
                <div
                  className="btn btn-safy-info float-end mx-1"
                  onClick={() => openPowerBI()}
                >
                  Power BI
                </div>
                <div
                  className="btn btn-safy-info float-end mx-1"
                  onClick={() => openDashboards()}
                >
                  Dashboard
                </div>
              </div>
            </div>
            <div className="mb-4">
              <div className="row bg-safy2-1 text-white">
                <div className="col-3  py-2">Item</div>
                <div className="col-4  py-2">Notes and Follow-Up</div>
                <div className="col-1  py-2"></div>
              </div>
              {data?.Results?.DocumentationFidelity &&
                data?.Results?.DocumentationFidelity?.map((item, index) => (
                  <div className="row border bg-white" key={`df${index}`}>
                    <div className="col-3 py-2">
                      <Select
                        name={`${index}dfItem`}
                        options={[
                          { id: "careplan", name: "Care Plan" },
                          { id: "encounters", name: "Encounters" },
                          { id: "assessments", name: "Assessments" },
                          { id: "ir", name: "IR's" },
                          { id: "forms", name: "Forms" },
                          { id: "ebp", name: "EBP Compliance" },
                          { id: "other", name: "Other" },
                        ]}
                        value={
                          data.Results.DocumentationFidelity[index]["item"]
                        }
                        onChange={(e) =>
                          handleChange(
                            e,
                            "DocumentationFidelity",
                            index,
                            "item"
                          )
                        }
                        disabled={!isSupervisor || supervisorSigned}
                      />
                      {data &&
                      data?.Results?.DocumentationFidelity &&
                      data.Results.DocumentationFidelity[index]["item"] ===
                        "Other" ? (
                        <Input
                          name={`${index}dfother`}
                          label="Other (specify)"
                          onChange={(e) =>
                            handleChange(
                              e,
                              "DocumentationFidelity",
                              index,
                              "other"
                            )
                          }
                          value={
                            data.Results.DocumentationFidelity[index]["other"]
                          }
                          disabled={!isSupervisor || supervisorSigned}
                        />
                      ) : null}
                    </div>
                    <div className="col-8 py-2">
                      <Input
                        name={`${index}dfnotes`}
                        label=""
                        onChange={(e) =>
                          handleChange(
                            e,
                            "DocumentationFidelity",
                            index,
                            "notes"
                          )
                        }
                        value={
                          data?.Results?.DocumentationFidelity[index]["notes"]
                        }
                        disabled={!isSupervisor || supervisorSigned}
                      />
                    </div>

                    <div className="col-1 py-2">
                      {isSupervisor && !supervisorSigned && (
                        <i
                          className="fas fa-times-circle fa-lg text-danger"
                          onClick={(e) => {
                            let obj = { ...data };
                            obj.Results.DocumentationFidelity.splice(index, 1);
                            setData(obj);
                          }}
                          disabled={!isSupervisor || supervisorSigned}
                        ></i>
                      )}
                    </div>
                  </div>
                ))}
              {isSupervisor && !supervisorSigned && (
                <div
                  className="btn btn-success mt-2"
                  onClick={() => addTableRow("DocumentationFidelity")}
                  disabled={!isSupervisor || supervisorSigned}
                >
                  Add
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {data?.Results?.AdministrativeSupervision == true && (
        <div>
          <div className="my-4 border rounded p-2 bg-light">
            <div className="h5 mb-4">Administrative Supervision</div>
            <div className="mx-4 mb-4">
              <div className="row bg-safy2-1 text-white">
                <div className="col-3  py-2">Item</div>
                <div className="col-4  py-2">Notes and Follow-Up</div>
                <div className="col-1  py-2"></div>
              </div>
              {data && data.Results.Administration
                ? data.Results.Administration.map((item, index) => {
                    return (
                      <div className="row border bg-white" key={`as${index}`}>
                        <div className="col-3 py-2">
                          <Select
                            name={`${index}asItem`}
                            options={[
                              { id: "utilization", name: "Utilization" },
                              { id: "trainingNeeds", name: "Training Needs" },
                              { id: "fileCompliance", name: "File Compliance" },
                              { id: "forms", name: "Forms" },
                              { id: "ebp", name: "EBP Compliance" },
                              { id: "concur", name: "Concur" },
                              { id: "adp", name: "ADP" },
                              { id: "performance", name: "Performance" },
                              { id: "draft", name: "Days to Draft Sign" },
                              { id: "powerBI", name: "Power BI" },
                              { id: "other", name: "Other" },
                            ]}
                            value={data.Results.Administration[index]["item"]}
                            onChange={(e) =>
                              handleChange(e, "Administration", index, "item")
                            }
                            disabled={!isSupervisor || supervisorSigned}
                          />
                          {data?.Results?.Administration[index]["item"] ===
                          "Other" ? (
                            <Input
                              name={`${index}asOther`}
                              label="Other (specify)"
                              onChange={(e) =>
                                handleChange(
                                  e,
                                  "Administration",
                                  index,
                                  "other"
                                )
                              }
                              value={
                                data.Results.Administration[index]["other"]
                              }
                              disabled={!isSupervisor || supervisorSigned}
                            />
                          ) : null}
                        </div>
                        <div className="col-8 py-2">
                          <Input
                            name={`${index}asnotes`}
                            label=""
                            onChange={(e) =>
                              handleChange(e, "Administration", index, "notes")
                            }
                            value={data.Results.Administration[index]["notes"]}
                            disabled={!isSupervisor || supervisorSigned}
                          />
                        </div>
                        <div className="col-1 py-2">
                          {isSupervisor && !supervisorSigned && (
                            <i
                              className="fas fa-times-circle fa-lg text-danger"
                              onClick={(e) => {
                                let obj = { ...data };
                                obj.Results.Administration.splice(index, 1);
                                setData(obj);
                              }}
                              disabled={!isSupervisor || supervisorSigned}
                            ></i>
                          )}
                        </div>
                      </div>
                    );
                  })
                : null}
              {isSupervisor && !supervisorSigned && (
                <div
                  className="btn btn-success mt-2"
                  onClick={() => addTableRow("Administration")}
                  disabled={!isSupervisor || supervisorSigned}
                >
                  Add
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {data?.Results?.ManagerialSupervision == true && (
        <div className="border rounded bg-light my-4 p-2">
          <div className="row mx-4">
            <div className="col-3">
              <div className="h5 mb-4">Managerial Supervision</div>
            </div>
            <div className="col-9">
              <div
                className="btn btn-safy-info float-end mx-1"
                onClick={() => openPowerBI()}
              >
                Power BI
              </div>
              <div
                className="btn btn-safy-info float-end mx-1"
                onClick={() => openDashboards()}
              >
                Dashboard
              </div>
            </div>
          </div>
          <div className="row bg-safy2-1 text-white mx-2 mt-2">
            <div className="col-3  py-2">People Managment</div>
            <div className="col-4  py-2">Notes and Follow-Up</div>
            <div className="col-1  py-2"></div>
          </div>
          {data && data.Results.PeopleManagement
            ? data.Results.PeopleManagement.map((item, index) => {
                return (
                  <div className="row border bg-white mx-2" key={`pm${index}`}>
                    <div className="col-3 py-2">
                      <Select
                        name={`${index}pmItem`}
                        options={[
                          { id: "Staffing Issues", name: "Staffing Issues" },
                          { id: "At-Risk Staff", name: "At-Risk Staff" },
                          { id: "Onboarding", name: "Onboarding" },
                          { id: "Staff Training,", name: "Staff Training" },
                          {
                            id: "Strengths and Celebrations",
                            name: "Strengths and Celebrations",
                          },

                          { id: "Other", name: "Other" },
                        ]}
                        value={data.Results.PeopleManagement[index]["item"]}
                        onChange={(e) =>
                          handleChange(e, "PeopleManagement", index, "item")
                        }
                        disabled={!isSupervisor || supervisorSigned}
                      />
                      {data?.Results?.PeopleManagement[index]["item"] ===
                      "Other" ? (
                        <Input
                          name={`${index}pmOther`}
                          label="Other (specify)"
                          onChange={(e) =>
                            handleChange(e, "PeopleManagement", index, "other")
                          }
                          value={data.Results.PeopleManagement[index]["other"]}
                          disabled={!isSupervisor || supervisorSigned}
                        />
                      ) : null}
                    </div>
                    <div className="col-8 py-2">
                      <Input
                        name={`${index}pmnotes`}
                        label=""
                        onChange={(e) =>
                          handleChange(e, "PeopleManagement", index, "notes")
                        }
                        value={data.Results.PeopleManagement[index]["notes"]}
                        disabled={!isSupervisor || supervisorSigned}
                      />
                    </div>
                    <div className="col-1 py-2">
                      {isSupervisor && !supervisorSigned && (
                        <i
                          className="fas fa-times-circle fa-lg text-danger"
                          onClick={(e) => {
                            let obj = { ...data };
                            obj.Results.PeopleManagement.splice(index, 1);
                            setData(obj);
                          }}
                          disabled={!isSupervisor || supervisorSigned}
                        ></i>
                      )}
                    </div>
                  </div>
                );
              })
            : null}
          {isSupervisor && !supervisorSigned && (
            <div
              className="btn btn-success mt-2 ms-4 mb-4"
              onClick={() => addTableRow("PeopleManagement")}
              disabled={!isSupervisor || supervisorSigned}
            >
              Add
            </div>
          )}
          <div className="row bg-safy2-1 text-white mx-2 mt-2">
            <div className="col-3  py-2">Program Managment</div>
            <div className="col-4  py-2">Notes and Follow-Up</div>
            <div className="col-1  py-2"></div>
          </div>
          {data && data.Results.ProgramManagement
            ? data.Results.ProgramManagement.map((item, index) => {
                return (
                  <div
                    className="row border bg-white mx-2"
                    key={`progm${index}`}
                  >
                    <div className="col-3 py-2">
                      <Select
                        name={`${index}pmItem`}
                        options={[
                          { id: "Program Updates", name: "Program Updates" },
                          { id: "Risks and Needs", name: "Risks and Needs" },
                          {
                            id: "Program Priorities",
                            name: "Program Priorities",
                          },
                          { id: "Census,", name: "Census" },
                          { id: "Financials,", name: "Financials" },
                          {
                            id: "Strengths and Celebrations",
                            name: "Strengths and Celebrations",
                          },

                          { id: "Other", name: "Other" },
                        ]}
                        value={data.Results.ProgramManagement[index]["item"]}
                        onChange={(e) =>
                          handleChange(e, "ProgramManagement", index, "item")
                        }
                        disabled={!isSupervisor || supervisorSigned}
                      />
                      {data?.Results?.ProgramManagement[index]["item"] ===
                      "Other" ? (
                        <Input
                          name={`${index}progmOther`}
                          label="Other (specify)"
                          onChange={(e) =>
                            handleChange(e, "ProgramManagement", index, "other")
                          }
                          value={data.Results.ProgramManagement[index]["other"]}
                          disabled={!isSupervisor || supervisorSigned}
                        />
                      ) : null}
                    </div>
                    <div className="col-8 py-2">
                      <Input
                        name={`${index}progmnotes`}
                        label=""
                        onChange={(e) =>
                          handleChange(e, "ProgramManagement", index, "notes")
                        }
                        value={data.Results.ProgramManagement[index]["notes"]}
                        disabled={!isSupervisor || supervisorSigned}
                      />
                    </div>
                    <div className="col-1 py-2">
                      {isSupervisor && !supervisorSigned && (
                        <i
                          className="fas fa-times-circle fa-lg text-danger"
                          onClick={(e) => {
                            let obj = { ...data };
                            obj.Results.ProgramManagement.splice(index, 1);
                            setData(obj);
                          }}
                          disabled={!isSupervisor || supervisorSigned}
                        ></i>
                      )}
                    </div>
                  </div>
                );
              })
            : null}
          {isSupervisor && !supervisorSigned && (
            <div
              className="btn btn-success mt-2 ms-4"
              onClick={() => addTableRow("ProgramManagement")}
              disabled={!isSupervisor || supervisorSigned}
            >
              Add
            </div>
          )}
        </div>
      )}
      <Input
        name="SelfCare"
        label="Self-Care/Wellness "
        onChange={handleChange}
        value={data.Results?.SelfCare}
        disabled={!isSupervisor || supervisorSigned}
      />
      <Input
        name="OtherComments"
        label="Other Comments"
        onChange={handleChange}
        value={data.Results?.OtherComments}
        disabled={!isSupervisor || supervisorSigned}
      />
      <div
        className="text-safy-info"
        style={{ cursor: "pointer" }}
        onClick={() =>
          window.open(
            "https://survey.alchemer.com/s3/6727286/Professional-Quality-of-Life-Measure-ProQOL-5-0"
          )
        }
      >
        Link to ProQOL-5
      </div>
      <Comments data={data} refresh={loadForm} />
      <Signatures
        data={data}
        isSupervisee={isSupervisee}
        isSupervisor={isSupervisor}
        handleSave={() => saveFormsyV2(data, "Update")}
        callback={loadForm}
        lockedOn="Supervisor"
      />
    </div>
  );
}

export default Individual;
